import { useFormGuard } from "../shared/UnsavedChangesGuard";
import { useState } from "react";
import {
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Alert,
  Collapse,
  Typography,
  Box,
} from "@mui/material";
import { Visibility, VisibilityOff, Lock, Edit } from "@mui/icons-material";
import ApiClient from "../../config/AppAdmin";

const PasswordChangeForm = ({ adminContact }) => {
  const [changePassword, setChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const guard = useFormGuard({ values: passwordData, saving: loading, enabled: changePassword });

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleShowPassword = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmitPasswordChange = async () => {
    if (!adminContact) {
      setMessage({ type: "error", text: "User info not available" });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: "error", text: "Passwords don't match" });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({
        type: "error",
        text: "Password must be at least 6 characters",
      });
      return;
    }

    if (!guard.beginSave()) return;
    setLoading(true);
    try {
      const res = await ApiClient.post("/admin/reset-password", {
        admin_contact: adminContact,
        new_password: passwordData.newPassword,
      });

      setMessage({
        type: res.data.success ? "success" : "error",
        text: res.data.message,
      });
      if (res.data.success) {
        guard.markSaved({ newPassword: "", confirmPassword: "" });
        setChangePassword(false);
        setPasswordData({ newPassword: "", confirmPassword: "" });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "An error occurred",
      });
    } finally {
      guard.endSave();
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
          <Lock sx={{ mr: 1 }} /> Account Security
        </Typography>
        <Button
          sx={{ width: { xs: "50%", sm: "40%", md: "30%" } }}
          startIcon={<Edit />}
          variant="outlined"
          onClick={() => guard.request(() => { setChangePassword(!changePassword); setPasswordData({ newPassword: "", confirmPassword: "" }); setMessage(null); })}
          disabled={loading}
        >
          {changePassword ? "Cancel" : "Change Password"}
        </Button>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Collapse in={changePassword}>
        <Stack spacing={2} sx={{ width: { xs: "80%", sm: "50%", md: "30%" } }}>
          <TextField
            label="New Password"
            name="newPassword"
            type={showPassword.new ? "text" : "password"}
            value={passwordData.newPassword}
            onChange={handlePasswordChange}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => toggleShowPassword("new")}>
                    {showPassword.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Confirm Password"
            name="confirmPassword"
            type={showPassword.confirm ? "text" : "password"}
            value={passwordData.confirmPassword}
            onChange={handlePasswordChange}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => toggleShowPassword("confirm")}>
                    {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmitPasswordChange}
            disabled={
              loading ||
              !passwordData.newPassword ||
              !passwordData.confirmPassword
            }
          >
            {loading ? "Updating..." : "Update Password"}
          </Button>
        </Stack>
      </Collapse>
    </Box>
  );
};

export default PasswordChangeForm;
